const prefix = 'molepool:mp:pvc:';
const profilesKey = 'molepool:mp:profiles';

function profileMetadata(storage) {
  try {
    const value = JSON.parse(storage.getItem(profilesKey) || '[]');
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value.filter(profile => {
      if (!/^MP[1-9A-HJ-NP-Za-km-z]{33}$/.test(profile?.id || '') || !['private', 'public'].includes(profile?.visibility) || seen.has(profile.id)) return false;
      seen.add(profile.id); return true;
    }).map(({ id, visibility }) => ({ id, visibility }));
  } catch { return []; }
}

export function savedGeneratedProfiles(storage) {
  try {
    const local = storage || globalThis.localStorage;
    return profileMetadata(local).map(profile => ({ ...profile, pvc: savedPVC(profile.id, local) }))
      .filter(profile => /^PVC-[A-Za-z0-9_-]{43}$/.test(profile.pvc));
  } catch { return []; }
}

export function saveGeneratedProfile(profile, storage) {
  if (!/^MP[1-9A-HJ-NP-Za-km-z]{33}$/.test(profile?.id || '') || !/^PVC-[A-Za-z0-9_-]{43}$/.test(profile?.pvc || '') || !['private', 'public'].includes(profile?.visibility)) return false;
  try {
    const local = storage || globalThis.localStorage;
    if (!savePVC(profile.id, profile.pvc, local)) return false;
    const previous = profileMetadata(local).filter(item => item.id !== profile.id);
    // Only public metadata goes in the index; PVC has one canonical storage key.
    local.setItem(profilesKey, JSON.stringify([{ id: profile.id, visibility: profile.visibility }, ...previous]));
    return true;
  } catch { return false; }
}

export function savedPVC(login, storage) {
  try { return (storage || globalThis.localStorage)?.getItem(prefix + login) || ''; } catch { return ''; }
}
export function savePVC(login, pvc, storage) {
  try { (storage || globalThis.localStorage).setItem(prefix + login, pvc); return true; } catch { return false; }
}
export function forgetPVC(login, storage) {
  try {
    const local = storage || globalThis.localStorage;
    local?.removeItem(prefix + login);
    const profiles = profileMetadata(local).filter(profile => profile.id !== login);
    if (profiles.length) local.setItem(profilesKey, JSON.stringify(profiles));
    else local?.removeItem(profilesKey);
  } catch { /* Storage may be unavailable. */ }
}

export function financialAccess(account, login) {
  return account?.miner === login && (account.identityType === 'direct' || account.financialAccess === true);
}

// Used on logout before another response can arrive: retain activity only.
export function publicActivity(account) {
  const fields = ['miner', 'identityType', 'visibility', 'currentHashrate', 'averageHashrate', 'workerTotal', 'workerOnline', 'workerOffline', 'chart'];
  const result = Object.fromEntries(fields.filter(key => key in account).map(key => [key, account[key]]));
  result.privateView = false; result.financialAccess = false;
  result.workers = (account.workers || []).map(worker => Object.fromEntries(['name', 'currentHashrate', 'averageHashrate', 'offline', 'lastShares'].filter(key => key in worker).map(key => [key, worker[key]])));
  return result;
}

export async function profileRequest(login, { page = 1, size = 15, pvc = '', fetcher = globalThis.fetch, signal } = {}) {
  let response;
  try {
    response = await fetcher(`/api/v1/account/${encodeURIComponent(login)}/${page}/${size}`, {
      cache: 'no-store', credentials: 'same-origin', signal, headers: pvc ? { 'X-Molepool-PVC': pvc } : {},
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new Error('api_unavailable');
  }
  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.status || body.result?.miner !== login) {
    const codes = { 401: 'invalid_pvc', 403: 'private_profile', 404: 'profile_not_found', 429: 'too_many_attempts' };
    const error = new Error(codes[response.status] || 'api_unavailable');
    if (response.status === 429) {
      const seconds = Number(response.headers.get('Retry-After'));
      error.retryAfter = Number.isFinite(seconds) && seconds > 0 ? Math.min(Math.ceil(seconds), 3600) : 60;
    }
    throw error;
  }
  return body.result;
}

export async function openPrivateProfile(login, pvc, options = {}) {
  let account;
  try {
    account = await profileRequest(login, { ...options, pvc });
    if (account.privateView !== true || !financialAccess(account, login)) throw new Error('invalid_pvc');
  } catch (error) {
    if (['invalid_pvc', 'profile_not_found'].includes(error.message)) forgetPVC(login, options.storage);
    throw error;
  }
  if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  if (options.save !== false && !savePVC(login, pvc, options.storage)) throw new Error('storage_unavailable');
  return account;
}

export async function loadProfile(login, options = {}) {
  let account;
  try { account = await profileRequest(login, options); }
  catch (error) {
    if (error.message === 'profile_not_found') forgetPVC(login, options.storage);
    throw error;
  }
  const pvc = account.identityType === 'mp' && account.visibility === 'private' ? savedPVC(login, options.storage) : '';
  if (!pvc) return { account, accessError: '' };
  try {
    const privateAccount = await openPrivateProfile(login, pvc, { ...options, save: false });
    return { account: savedPVC(login, options.storage) === pvc ? privateAccount : account, accessError: '' };
  }
  catch (error) {
    if (error.name === 'AbortError') throw error;
    return { account, accessError: error.message, ...(error.retryAfter ? { retryAfter: error.retryAfter } : {}) };
  }
}
